import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, Trash2, Loader2, CheckCircle2, Mail, Layers, Images } from "lucide-react";
import HoverPreview from "./HoverPreview";
import { FileThumb } from "./FileThumb";
import AttachmentViewer from "./AttachmentViewer";
import { useGallery } from "./AttachmentStrip";
import { fileKey } from "../lib/attachments";
import { PENDING_SLOT_KEY } from "./MissingDocsCard";
import { toast } from "sonner";
import { api } from "../lib/api";

const ACCEPT = "application/pdf,image/*";

function FileLine({ dossierId, f, onDelete, onOpen, testid }) {
  const origin = f.kind === "email" ? "reçu par email" : f.kind === "tab" ? `onglet ${f.from_field}` : f.signed ? `signé via Yousign le ${new Date(f.uploaded_at).toLocaleDateString("fr-FR")}` : f.generated ? `généré et envoyé le ${new Date(f.uploaded_at).toLocaleDateString("fr-FR")}` : "ajouté manuellement";
  return (
    <div className="flex items-center gap-2 text-xs py-1 min-w-0" data-testid={testid}>
      <HoverPreview dossierId={dossierId} file={f} className="flex-shrink-0" testid={`${testid}-preview`}>
        <button onClick={() => onOpen(f)} className="w-9 h-11 block rounded border border-norea-border overflow-hidden hover:ring-2 hover:ring-norea-primary transition-shadow" data-testid={`${testid}-thumb`}>
          <FileThumb dossierId={dossierId} file={f} />
        </button>
      </HoverPreview>
      {f.kind === "email" ? <Mail className="w-3 h-3 text-norea-textDisabled flex-shrink-0" /> : f.kind === "tab" ? <Layers className="w-3 h-3 text-norea-textDisabled flex-shrink-0" /> : null}
      <button onClick={() => onOpen(f)} className="truncate min-w-0 text-left text-norea-text hover:text-norea-primary hover:underline" title={f.filename}>{f.filename}</button>
      <span className="text-norea-textDisabled whitespace-nowrap">· {origin}</span>
      {f.kind === "manual" && (
        <button onClick={() => onDelete(f)} className="ml-auto p-1 rounded text-norea-textMuted hover:text-red-600" title="Supprimer" data-testid={`${testid}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
      )}
    </div>
  );
}

function Slot({ dossierId, slot, onChanged, onOpen }) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  const tid = `doc-slot-${slot.id}`;

  const upload = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type === "application/pdf" || f.type.startsWith("image/"));
    if (!list.length) return toast.error("Formats acceptés : PDF ou images");
    setBusy(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("field_id", slot.id);
        fd.append("file", file);
        await api.post(`/dossiers/${dossierId}/tab-data/documents/attachments`, fd);
      }
      toast.success(`${list.length} fichier(s) ajouté(s) à « ${slot.label} »`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Supprimer « ${f.filename} » ?`)) return;
    await api.delete(`/dossiers/${dossierId}/tab-data/documents/attachments/${f.id}`);
    toast.success("Fichier supprimé");
    onChanged();
  };

  const filled = slot.files.length > 0;
  return (
    <div className="grid grid-cols-[170px_minmax(0,1fr)] gap-4 items-start min-w-0 rounded-md transition-shadow" data-testid={tid} data-slot-label={(slot.label || "").toLowerCase()}>
      <div className="pt-2 text-sm text-norea-text flex items-start gap-1.5">
        {filled ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> : <span className="w-4 h-4 rounded-full border border-norea-border flex-shrink-0 mt-0.5" />}
        <span>{slot.label}{slot.required && <span className="text-red-500 ml-0.5" title="Obligatoire">*</span>}
          {slot.phase && <span className="block text-[10px] text-norea-textMuted" data-testid={`slot-phase-${slot.id}`}>{slot.phase}</span>}</span>
      </div>
      <div className={`min-w-0 rounded-lg border bg-white p-2 ${filled ? "border-emerald-200" : "border-norea-border"}`}>
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); upload(e.dataTransfer.files); }}
          className={`cursor-pointer rounded-md px-3 py-2.5 text-sm flex items-center justify-center gap-2 transition-colors ${over ? "bg-emerald-200 text-emerald-950" : "bg-emerald-50 text-emerald-900 hover:bg-emerald-100"}`}
          data-testid={`${tid}-dropzone`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Glissez ici vos fichiers…
        </div>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid={`${tid}-input`} />
        <div className="px-1 pt-1.5">
          {filled ? slot.files.map((f, i) => <FileLine key={`${f.kind}-${f.idx ?? f.id}`} dossierId={dossierId} f={f} onDelete={remove} onOpen={onOpen} testid={`${tid}-file-${i}`} />) : (
            <div className="text-center text-xs font-medium text-norea-textMuted py-1" data-testid={`${tid}-empty`}>Aucun document disponible</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DossierDocumentsBoard({ dossierId, onChanged }) {
  const [board, setBoard] = useState(null);
  const load = useCallback(() => api.get(`/dossiers/${dossierId}/documents-board`).then((r) => setBoard(r.data)).catch(() => setBoard({ slots: [], others: [] })), [dossierId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const label = sessionStorage.getItem(PENDING_SLOT_KEY);
    if (!board || !label) return;
    sessionStorage.removeItem(PENDING_SLOT_KEY);
    const el = [...document.querySelectorAll("[data-slot-label]")].find((e) => e.dataset.slotLabel === label.toLowerCase())
      || [...document.querySelectorAll("[data-slot-label]")].find((e) => e.dataset.slotLabel.includes(label.toLowerCase().slice(0, 8)));
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-red-400", "ring-offset-4");
      setTimeout(() => el.classList.remove("ring-2", "ring-red-400", "ring-offset-4"), 2500);
    }, 150);
  }, [board]);
  const [gallery, reloadGallery] = useGallery(dossierId);
  const [openKey, setOpenKey] = useState(null);
  const changed = () => { load(); reloadGallery(); onChanged?.(); };
  const open = (f) => setOpenKey(fileKey(f));

  if (!board) return <div className="text-sm text-norea-textMuted">Chargement…</div>;
  return (
    <div className="space-y-5" data-testid="documents-board">
      <div className="text-xs text-norea-textMuted" data-testid="documents-board-count">
        <b className="text-norea-text">{board.filled}/{board.total}</b> documents présents — les pièces reçues par email et celles jointes dans les autres onglets se rangent automatiquement.
        {gallery?.length > 0 && (
          <button onClick={() => setOpenKey(gallery[0].key)} className="ml-3 inline-flex items-center gap-1 text-norea-primary hover:underline" data-testid="documents-board-open-viewer">
            <Images className="w-3.5 h-3.5" /> Parcourir toutes les pièces ({gallery.length})
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-8 gap-y-3 [&>*]:min-w-0">
        {board.slots.filter((s) => !s.hidden || s.files.length).map((s) => <Slot key={s.id} dossierId={dossierId} slot={s} onChanged={changed} onOpen={open} />)}
      </div>
      {board.others.length > 0 && (
        <div className="card-flat p-4" data-testid="documents-board-others">
          <div className="overline mb-2">Autres documents du dossier</div>
          {board.others.map((f, i) => <FileLine key={`${f.kind}-${f.idx ?? f.id}`} dossierId={dossierId} f={f} onOpen={open} testid={`doc-other-${i}`} />)}
        </div>
      )}
      {openKey && gallery && <AttachmentViewer dossierId={dossierId} items={gallery} startKey={openKey} onClose={() => setOpenKey(null)} />}
    </div>
  );
}
